import type * as React from 'react'
import { Link as RouterLink, useMatch } from 'react-router-dom'

import {
  ArrowUpIcon,
  Flex,
  Heading,
  HoverCard,
  InfoIcon,
  Link,
  Text,
  TrendingUpIcon,
  TargetIcon,
  MessageSquareIcon,
  ThumbsUpIcon,
  EyeIcon,
  Repeat2Icon,
  VisuallyHidden,
  MessageSquareQuoteIcon,
  StarIcon,
  Share2Icon,
  MousePointerClickIcon,
  BookmarkIcon,
  ActivityIcon,
  UserRoundPlusIcon,
  CircleDollarSignIcon,
} from '@buffer-mono/popcorn'
import type { IconProps } from '@buffer-mono/popcorn'

import type {
  PostMetricType,
  PostMetricUnit,
} from '~publish/components/graphqlEnums'

import { commentsChannel } from '../../../legacy/routes'

import styles from './PostMetric.module.css'

import type { JSX } from 'react'

const formatMetricNumber = (
  value: number,
  unit: PostMetricUnit,
): string | null => {
  const roundedValue = Math.round(value)

  if (unit === 'percentage') {
    return `${roundedValue}%`
  }

  return roundedValue.toLocaleString('en-US')
}

const iconByType: Partial<
  Record<PostMetricType, React.ComponentType<IconProps>>
> = {
  engagementRate: ActivityIcon,
  favorites: StarIcon,
  impressions: TargetIcon,
  likes: ThumbsUpIcon,
  reach: ArrowUpIcon,
  shares: Share2Icon,
  views: EyeIcon,
  reactions: ThumbsUpIcon,
  replies: MessageSquareIcon,
  comments: MessageSquareQuoteIcon,
  reposts: Repeat2Icon,
  reblogs: Repeat2Icon,
  repins: Repeat2Icon,
  retweets: Repeat2Icon,
  link_clicks: MousePointerClickIcon,
  clicks: MousePointerClickIcon,
  saves: BookmarkIcon,
  freeSubscriptions: UserRoundPlusIcon,
  paidSubscriptions: CircleDollarSignIcon,
}

type PostMetricProps = {
  name: string
  type: PostMetricType
  description?: string | null
  value: number | null | undefined
  unit: PostMetricUnit
  engageUrl: string | null
}

const PostMetric = ({
  name,
  type,
  description,
  value,
  unit,
  engageUrl,
}: PostMetricProps): JSX.Element => {
  const Icon = iconByType[type] ?? TrendingUpIcon
  const linkToEngage = type === 'comments' && Number(value) > 0 && engageUrl

  const noData = (
    <>
      <VisuallyHidden>no data available</VisuallyHidden>
      <span aria-hidden>-</span>
    </>
  )

  const hasData = value !== null && value !== undefined

  const displayValue = hasData ? formatMetricNumber(value, unit) : noData

  const commentsPageMatch = useMatch(commentsChannel.postsRoute)
  const isCommentsPage = !!commentsPageMatch

  return (
    <div className={styles.wrapper}>
      <div className={styles.label}>
        <Icon aria-hidden={true} color="subtle" />
        <Text weight="medium">
          {linkToEngage && !isCommentsPage ? (
            <Link as={RouterLink} to={linkToEngage} data-testid="engage-cta">
              {name}
            </Link>
          ) : (
            name
          )}
        </Text>
        {description ? (
          <HoverCard openDelay="fast">
            <HoverCard.Trigger>
              <button
                type="button"
                aria-label={`About ${name}`}
                className={styles.infoButton}
              >
                <InfoIcon color="subtle" />
              </button>
            </HoverCard.Trigger>
            <HoverCard.Content
              side="top"
              align="center"
              arrow
              className={styles.hoverCardContent}
            >
              <Flex direction="column" gap="xs">
                <Heading size="sm" as="h4">
                  {name}
                </Heading>
                <Text color="subtle">{description}</Text>
              </Flex>
            </HoverCard.Content>
          </HoverCard>
        ) : null}
      </div>
      <Text className={styles.metric} weight="bold" size="md">
        {displayValue}
      </Text>
    </div>
  )
}

PostMetric.displayName = 'PostMetric'

export { PostMetric }
